import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {AllowanceEntity, BokkzTslDataService, GezinEninkomenPerMaand} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {
  debounceTime,
  filter,
  map,
  Observable,
  pairwise,
  shareReplay,
  startWith,
  Subscription,
  switchMap,
  take,
  tap
} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from "@snelstart/ui-alert";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type InkomenFormGroup = FormGroup<{
  maand: FormControl<number | null>;
  toeslagPartner: FormControl<boolean | null>;
  geboortedatumPartner: FormControl<Date | null>;
  bsnPartner: FormControl<string | null>;
  toetsinkomenAangever: FormControl<number | null>;
  toetsinkomenPartner: FormControl<number | null>;
  toetsvermogenAangever: FormControl<number | null>;
  toetsvermogenPartner: FormControl<number | null>;
  aangeverHeeftZorgverzekering: FormControl<boolean | null>;
  partnerHeeftZorgverzekering: FormControl<boolean | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-partner',
  templateUrl: './partner.component.html',
  styleUrls: ['./partner.component.scss'],
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class PartnerComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzTslDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public year: any;
  public hasPartner = false;

  public formGroup = new FormGroup({
    inkomensgegevens: new FormArray<InkomenFormGroup>([])
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];
  private readonly partnerDetailsKeys: Array<
    'bsnPartner' | 'geboortedatumPartner' | 'toetsinkomenPartner' | 'toetsvermogenPartner' | 'partnerHeeftZorgverzekering'
  > = ['bsnPartner', 'geboortedatumPartner', 'toetsinkomenPartner', 'toetsvermogenPartner', 'partnerHeeftZorgverzekering'];

  readonly allowanceEntity$: Observable<AllowanceEntity> =
    this.dataService.allowanceEntity$.pipe(
      filter((data): data is AllowanceEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.allowanceEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.year = data.year;
      this.hasPartner = data.toeslagen?.inkomensgegevens?.some(ig => ig.toeslagPartner) ?? false;
      this.updateForm(data.toeslagen?.inkomensgegevens || []);
    })
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  public getLabel(index: number): any {
    if (index === 1) {
      return "Januari";
    }
    if (index === 2) {
      return "Februari";
    }
    if (index === 3) {
      return "Maart";
    }
    if (index === 4) {
      return "April";
    }
    if (index === 5) {
      return "Mei";
    }
    if (index === 6) {
      return "Juni";
    }
    if (index === 7) {
      return "Juli";
    }
    if (index === 8) {
      return "Augustus";
    }
    if (index === 9) {
      return "September";
    }
    if (index === 10) {
      return "Oktober";
    }
    if (index === 11) {
      return "November";
    }
    if (index === 12) {
      return "December";
    }
  }

  get inkomensGegevensList(): FormArray<InkomenFormGroup> {
    return this.formGroup.controls.inkomensgegevens;
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractInputForCalculation = () =>
      this.inkomensGegevensList.getRawValue().map((item) => ({
        toetsinkomenPartner: item.toetsinkomenPartner,
        toetsvermogenPartner: item.toetsvermogenPartner,
        partnerHeeftZorgverzekering: item.partnerHeeftZorgverzekering,
      }));

    this.changeSubscriptions.add(
      this.formGroup.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          map(() => extractInputForCalculation()),
          startWith(extractInputForCalculation()),
          pairwise(),
          filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
          switchMap(() => this.dataService.calculateLatestAllowances()),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(),
    );
  }

  createFormGroup(int: number, item?: GezinEninkomenPerMaand): InkomenFormGroup {
    const group = new FormGroup({
      maand: new FormControl<number | null>(item?.maand ?? int),
      toeslagPartner: new FormControl<boolean | null>(item?.toeslagPartner ?? null),
      geboortedatumPartner: new FormControl<Date | null>(item?.geboortedatumPartner ?? null),
      bsnPartner: new FormControl<string | null>(item?.bsnPartner ?? null),
      toetsinkomenAangever: new FormControl<number | null>(item?.toetsinkomenAangever ?? null),
      toetsinkomenPartner: new FormControl<number | null>(item?.toetsinkomenPartner ?? null),
      toetsvermogenAangever: new FormControl<number | null>(item?.toetsvermogenAangever ?? null),
      toetsvermogenPartner: new FormControl<number | null>(item?.toetsvermogenPartner ?? null),
      aangeverHeeftZorgverzekering: new FormControl<boolean | null>(item?.aangeverHeeftZorgverzekering ?? null),
      partnerHeeftZorgverzekering: new FormControl<boolean | null>(item?.partnerHeeftZorgverzekering ?? null),
    });
    this.setupPartnerToggleSync(group);
    return group;
  }

  private updateForm(data : GezinEninkomenPerMaand[]): void {
    const formArray = this.inkomensGegevensList;
    const byMonth = new Map<number, GezinEninkomenPerMaand>();
    (data || []).forEach((item, idx) => {
      const m = item?.maand ?? idx + 1;
      if (m >= 1 && m <= 12) {
        byMonth.set(m, item);
      }
    });

    while (formArray.length > 12) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let m = 1; m <= 12; m++) {
      const item = byMonth.get(m);
      if (m - 1 < formArray.length) {
        const control = formArray.at(m - 1) as InkomenFormGroup;
        control.patchValue({maand: m, ...(item ?? {})}, {emitEvent: false});
        this.setPartnerDetailsDisabledState(control, control.controls.toeslagPartner.value === false);
      } else {
        formArray.push(this.createFormGroup(m, item), {emitEvent: false});
      }
    }
  }

  private setupPartnerToggleSync(group: InkomenFormGroup): void {
    group.controls.toeslagPartner.valueChanges.pipe(
      startWith(group.controls.toeslagPartner.value),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((hasToeslagPartner) => {
      this.setPartnerDetailsDisabledState(group, hasToeslagPartner === false);
    });
  }

  private setPartnerDetailsDisabledState(group: InkomenFormGroup, disabled: boolean): void {
    this.partnerDetailsKeys.forEach((key) => {
      const control = group.controls[key];
      if (disabled) {
        control.disable({emitEvent: false});
      } else {
        control.enable({emitEvent: false});
      }
    });
  }

  copyFromPrevious(index: number): void {
    if (index <= 0) return;

    const previousData = { ...this.inkomensGegevensList.at(index - 1).value };
    previousData.maand = index + 1;

    this.inkomensGegevensList.at(index).patchValue(previousData);
    this.saveForm();
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    setTimeout(() => {
      const rawValue = this.formGroup.getRawValue();
      this.dataService.patchAllowanceEntity((currentEntity) => {
        const toeslagen: any = {
          ...currentEntity.toeslagen,
          inkomensgegevens: rawValue.inkomensgegevens ?? undefined,
        };
        return {
          ...currentEntity,
          toeslagen,
        };
      });
    },0)
  }
}
